/**
 * File list component for displaying multiple files
 */

import { forwardRef } from 'react';
import { Stack, Grid, Text } from '@/components/ui';
import { FileCard } from './FileCard';
import type { FileListProps } from '../types';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

/**
 * File list component for displaying multiple files in grid or list layout
 *
 * @example
 * ```tsx
 * <FileList
 *   files={files}
 *   layout="grid"
 *   onDownload={handleDownload}
 *   onDelete={handleDelete}
 *   onPreview={handlePreview}
 * />
 * ```
 */
export const FileList = forwardRef<HTMLDivElement, FileListProps>(
  (props, ref) => {
    const {
      files,
      onDownload,
      onDelete,
      onPreview,
      layout = 'list',
      emptyMessage = 'No files found',
      loading = false,
    } = props;

    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    if (loading) {
      return (
        <Stack gap="md" ref={ref}>
          <Text c="dimmed" style={accessibilityStyles}>Loading files...</Text>
        </Stack>
      );
    }

    if (!files || files.length === 0) {
      return (
        <Stack gap="md" align="center" ref={ref} style={{ padding: '2rem' }}>
          <Text c="dimmed" style={accessibilityStyles}>{emptyMessage}</Text>
        </Stack>
      );
    }

    // Grid layout
    if (layout === 'grid') {
      return (
        <Grid ref={ref}>
          {files.map((file) => (
            <Grid.Col key={file.id} span={{ base: 12, sm: 6, md: 4, lg: 3 }}>
              <FileCard
                file={file}
                onDownload={onDownload}
                onDelete={onDelete}
                onPreview={onPreview}
                variant="compact"
              />
            </Grid.Col>
          ))}
        </Grid>
      );
    }

    // List layout
    return (
      <Stack gap="sm" ref={ref}>
        {files.map((file) => (
          <FileCard
            key={file.id}
            file={file}
            onDownload={onDownload}
            onDelete={onDelete}
            onPreview={onPreview}
            variant="compact"
          />
        ))}
      </Stack>
    );
  }
);

FileList.displayName = 'FileList';

export default FileList;
